import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, NamedLink } from '../../../components';

import css from './LoginForm.module.css';
import Icons from '../../../components/Icons/Icons';

const LoginFormComponent = props => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          intl,
          hasValidationErrors,
          values,
          errors,
          submitError,
          submitFailed,
        } = fieldRenderProps;

        // Email
        const emailLabel = intl.formatMessage({ id: 'LoginForm.emailLabel' });
        const emailPlaceholder = intl.formatMessage({ id: 'LoginForm.emailPlaceholder' });
        const emailRequired = validators.required(
          intl.formatMessage({ id: 'LoginForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'LoginForm.emailInvalid' })
        );

        // Password
        const passwordLabel = intl.formatMessage({ id: 'LoginForm.passwordLabel' });
        const passwordPlaceholder = intl.formatMessage({ id: 'LoginForm.passwordPlaceholder' });
        const passwordRequired = validators.requiredStringNoTrim(
          intl.formatMessage({ id: 'LoginForm.passwordRequired' })
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = hasValidationErrors || submitInProgress;

        const passwordRecoveryLink = (
          <NamedLink
            name="PasswordRecoveryPage"
            className={css.recoveryLink}
            to={{
              search:
                values?.email && !errors?.email ? `email=${encodeURIComponent(values.email)}` : '',
            }}
          >
            <FormattedMessage id="LoginForm.forgotPassword" />
          </NamedLink>
        );

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div>
              <FieldTextInput
                className={css.formField}
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <div className={css.passwordWrapper}>
                <FieldTextInput
                  className={classNames(css.formField, css.passwordInput)}
                  type={showPassword ? 'text' : 'password'}
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="current-password"
                  label={passwordLabel}
                  placeholder={passwordPlaceholder}
                  validate={passwordRequired}
                />

                <button
                  type="button"
                  className={css.passwordToggle}
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Icons name="eyeOpen" /> : <Icons name="eyeClose" />}
                </button>
              </div>
            </div>

            {submitFailed && submitError && (
              <div className={css.errorMessage} role="alert">
                <FormattedMessage id="AuthenticationPage.loginFailed" />
              </div>
            )}

            <div className={css.bottomWrapper}>
              <p className={css.bottomWrapperText}>
                <span className={css.recoveryLinkInfo}>
                  <FormattedMessage
                    id="LoginForm.forgotPasswordInfo"
                    values={{ passwordRecoveryLink }}
                  />
                </span>
              </p>

              <PrimaryButton
                rootClassName={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="LoginForm.logIn" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the login form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @returns {JSX.Element}
 */
const LoginForm = props => {
  const intl = useIntl();
  return <LoginFormComponent {...props} intl={intl} />;
};

export default LoginForm;
